import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    
    const grid = this.querySelector('.media-gallery__grid');
    if (grid) {
      grid.addEventListener('touchstart', this.#handleGridTouchStart, { passive: true, signal });
      grid.addEventListener('touchmove', this.#handleGridTouchMove, { passive: false, signal });
      grid.addEventListener('touchend', this.#handleGridTouchEnd, { passive: true, signal });
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  #gridTouchStartX = 0;
  #gridTouchStartY = 0;
  #gridSwipingHorizontal = null;

  #handleGridTouchStart = (event) => {
    if (event.touches.length > 1) return;
    this.#gridTouchStartX = event.touches[0].clientX;
    this.#gridTouchStartY = event.touches[0].clientY;
    
    const grid = this.querySelector('.media-gallery__grid');
    if (!grid) return;
    this.#gridSwipingHorizontal = null;
  };

  #handleGridTouchMove = (event) => {
    const grid = this.querySelector('.media-gallery__grid');
    if (!grid || event.touches.length > 1 || this.#gridSwipingHorizontal === false) return;

    if (event.target && event.target.closest && event.target.closest('model-viewer')) return;

    const currentX = event.touches[0].clientX;
    const currentY = event.touches[0].clientY;
    const deltaX = this.#gridTouchStartX - currentX;
    const deltaY = this.#gridTouchStartY - currentY;

    if (this.#gridSwipingHorizontal === null) {
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 5) {
        this.#gridSwipingHorizontal = true;
      } else if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 5) {
        this.#gridSwipingHorizontal = false;
        return;
      } else {
        return;
      }
    }

    if (this.#gridSwipingHorizontal) {
      const firstSlide = grid.querySelector('.product-media-container');
      if (!firstSlide) return;
      const slideWidth = firstSlide.offsetWidth;
      
      // Dynamic momentum killer for Grid
      // Wait until they swipe ~85% of an image
      if (Math.abs(deltaX) >= slideWidth * 0.85) {
          if (grid.style.overflowX !== 'hidden') {
              grid.style.overflowX = 'hidden';
          }
      } else {
          if (grid.style.overflowX === 'hidden') {
              grid.style.overflowX = '';
          }
      }
    }
  };

  #handleGridTouchEnd = (event) => {
    const grid = this.querySelector('.media-gallery__grid');
    if (!grid || this.#gridSwipingHorizontal !== true) return;
    
    this.#gridSwipingHorizontal = null;
    
    // Restore overflow gracefully so native snap seamlessly guides it with zero starting momentum
    requestAnimationFrame(() => {
        grid.style.overflowX = ''; 
    });
  };
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
